// Components
import Switch, { SwitchProps } from "@mui/material/Switch";
import Box from "@mui/material/Box";
import { styled } from "@mui/material/styles";

type CustomSwitchProps = SwitchProps & {
  small?: boolean;
};

// Switch's default root is a fixed 58x38.
const ROOT_WIDTH = 58;
const ROOT_HEIGHT = 38;
const SMALL_SCALE = 0.7;

const StyledSwitch = styled(Switch)(() => ({
  padding: 8,
  "& .MuiSwitch-switchBase": {
    "&.Mui-checked": {
      "& + .MuiSwitch-track": {
        opacity: 1,
        backgroundColor: "#E2772E", // checked track color for enabled switch
      },
    },
    "&.Mui-disabled": {
      "& + .MuiSwitch-track": {
        opacity: 1,
        backgroundColor: "#435B6D7A", // unchecked track color for disabled switch
      },
    },
    "&.Mui-checked.Mui-disabled": {
      "& + .MuiSwitch-track": {
        opacity: 1,
        backgroundColor: "#E2772E70", // checked track color for disabled switch
      },
    },
  },
  "& .MuiSwitch-track": {
    borderRadius: 11,
    backgroundColor: "#5B6D7A", // unchecked track color
    opacity: 1,
    "&::before, &::after": {
      content: '""',
      position: "absolute",
      top: "50%",
      transform: "translateY(-50%)",
      width: 16,
      height: 16,
    },
  },
  "& .MuiSwitch-thumb": {
    boxShadow: "none",
    width: 16,
    height: 16,
    margin: 2,
    color: "#FFF",
  },
}));

const CustomSwitch = ({ small, sx, ...switchProps }: CustomSwitchProps) => {
  // sx was previously always replaced by the small-scale transform below
  // (or dropped to undefined) instead of merging with it -- an sx a caller
  // passed in never actually rendered. Composing as an array lets MUI
  // merge both instead of the second one clobbering the first.
  const switchElement = (
    <StyledSwitch
      {...switchProps}
      sx={[
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
        ...(small
          ? [
              {
                transform: `scale(${SMALL_SCALE})`,
                transformOrigin: "left top",
              },
            ]
          : []),
      ]}
    />
  );

  if (!small) {
    return switchElement;
  }

  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "flex-start",
        width: ROOT_WIDTH * SMALL_SCALE,
        height: ROOT_HEIGHT * SMALL_SCALE,
        overflow: "hidden",
      }}
    >
      {switchElement}
    </Box>
  );
};

export default CustomSwitch;
